/**
 * Word-level text diff for the HITL inline diff view.
 *
 * Tokenizes on whitespace but keeps the whitespace runs as tokens, so
 * concatenating segments reproduces the inputs byte-for-byte (multi-space and
 * newlines in textareas survive). Two invariants hold for every result:
 *
 * - Join of non-'add' segment text === before
 * - Join of non-'del' segment text === after
 */

export interface DiffSegment {
  type: 'same' | 'add' | 'del';
  text: string;
}

/**
 * Above this many DP cells (~500×500 tokens) the LCS table is not worth the
 * memory/latency in a modal; fall back to one del + one add block between the
 * common prefix/suffix.
 */
const MAX_LCS_CELLS = 250_000;

const tokenize = (s: string): string[] => s.split(/(\s+)/).filter(Boolean);

/** Merge adjacent segments of the same type so styling spans stay small. */
function pushSegment(
  out: DiffSegment[],
  type: DiffSegment['type'],
  text: string
): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.type === type) last.text += text;
  else out.push({ type, text });
}

/** Longest-common-subsequence diff over the token arrays. */
function lcsSegments(a: string[], b: string[]): DiffSegment[] {
  // dp[i][j] = LCS length of a[i..] vs b[j..]
  const rows = a.length + 1;
  const cols = b.length + 1;
  const dp: Uint32Array[] = Array.from(
    { length: rows },
    () => new Uint32Array(cols)
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pushSegment(out, 'same', a[i]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      pushSegment(out, 'del', a[i]);
      i += 1;
    } else {
      pushSegment(out, 'add', b[j]);
      j += 1;
    }
  }
  while (i < a.length) pushSegment(out, 'del', a[i++]);
  while (j < b.length) pushSegment(out, 'add', b[j++]);
  return out;
}

/**
 * Diff `before` → `after` at word granularity. Deletions ('del') carry text
 * only in `before`; additions ('add') only in `after`; 'same' text is in both.
 */
export function wordDiff(before: string, after: string): DiffSegment[] {
  if (before === after) {
    return before ? [{ type: 'same', text: before }] : [];
  }
  const a = tokenize(before);
  const b = tokenize(after);

  // Trim common prefix/suffix token runs — makes the common "one sentence
  // changed inside a long summary" case near-linear.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) {
    start += 1;
  }
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  const out: DiffSegment[] = [];
  pushSegment(out, 'same', a.slice(0, start).join(''));

  if (midA.length * midB.length > MAX_LCS_CELLS) {
    pushSegment(out, 'del', midA.join(''));
    pushSegment(out, 'add', midB.join(''));
  } else {
    for (const seg of lcsSegments(midA, midB)) {
      pushSegment(out, seg.type, seg.text);
    }
  }

  pushSegment(out, 'same', a.slice(endA).join(''));
  return out;
}
